import React, { ReactNode, useEffect, useMemo, useState } from 'react';
import { Modal, Input, Table, useDisclosure, Tbody } from '@chakra-ui/react';
import { TableHeader } from './table-header';
import { TableRow } from './table-row';
import { Paginator } from './paginator';
import { EmptyTable } from './empty-table';
import { getPageRows, getPaginatorOptions, Row, RowContents, Rows } from './helpers';
import { isDefined } from 'app/types/helpers';
import { Option } from 'app/helpers/form-helper';

const defaultComparator = (sortColumn: string, row: Row) => {
  return row[sortColumn].sortValue || row[sortColumn].data;
};

interface DataTableProps {
  rows: Rows;
  hasFilter?: boolean;
  hasPagination?: boolean;
  pageSize?: number;
  onSelectRows?: (rows: RowContents[]) => void;
  className?: string;
  sortComparator?: (sortColumn: any, row: any) => any;
  defaultSortColumn?: string;
  defaultSortAscending?: boolean;
}

export const DataTable: React.FC<DataTableProps> = ({
  rows = [],
  hasFilter = false,
  hasPagination = false,
  pageSize = 30,
  defaultSortColumn = undefined,
  defaultSortAscending = true,
  onSelectRows,
  className,
  sortComparator,
}) => {
  const [preview, setPreview] = useState<ReactNode>(null);
  const [selectedRows, setSelectedRows] = useState<RowContents[]>([]);
  const [filter, setFilter] = useState<string>('');
  const [page, setPage] = useState<Option<number, number>>({ value: 0, label: 1 });
  const [sortColumn, setSortColumn] = useState<string | undefined>(defaultSortColumn);
  const [sortAscending, setSortAscending] = useState<boolean>(defaultSortAscending);
  const [columnOrder, setColumnOrder] = useState<string[]>(Object.keys(rows[0]) || []);

  const { isOpen, onOpen, onClose } = useDisclosure();

  useEffect(() => {
    setColumnOrder(Object.keys(rows[0]));
  }, [rows]);

  const handlePreview = (Preview: React.ElementType<any>, previewProps: Record<string, string> | undefined) => {
    setPreview(<Preview {...previewProps} />);
    onOpen();
  };

  const handlePreviewClose = () => {
    setPreview(null);
    onClose();
  };

  const handleSelectRows = (newSelectedRows: RowContents[]) => {
    setSelectedRows(newSelectedRows);
    onSelectRows?.(newSelectedRows);
  };

  const handleToggleSingle = (row: RowContents) => {
    const selectedSet = new Set(selectedRows);
    if (selectedSet.has(row)) {
      selectedSet.delete(row);
    } else {
      selectedSet.add(row);
    }

    handleSelectRows([...selectedSet]);
  };

  const handleToggleAll = (e: React.ChangeEvent<HTMLInputElement>) => {
    const allRowIds = e.target.checked ? rows.map((r) => r.checkboxId).filter(isDefined) : [];
    handleSelectRows(allRowIds);
  };

  const isRowChecked = (row: RowContents) => {
    if (!row) {
      return selectedRows.length === rows.length;
    }
    return selectedRows.includes(row);
  };

  const handleFilter = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFilter(e.target.value);
  };

  const handlePaginate = (option: Option<number, number>) => {
    setPage(option);
  };

  const handleSort = (colName: string) => {
    setPage({ value: 0, label: 1 });
    setSortColumn((prevState) => {
      setSortAscending(prevState === colName ? !sortAscending : true);
      return colName;
    });
  };

  const filterRows = (row: Row) => {
    return Object.entries(row).some(([_, value]) => {
      const rowData = typeof value.data === 'string' ? value.data.toLowerCase() : '';
      return rowData.includes(filter.toLowerCase());
    });
  };

  const filteredRows = useMemo(() => {
    let updatedRows = [...rows];

    if (filter) {
      updatedRows = updatedRows.filter(filterRows);
    }

    if (sortColumn) {
      const sortingFunc = sortComparator || defaultComparator;
      updatedRows = updatedRows.sort((row) => sortingFunc(sortColumn, row));

      if (!sortAscending) {
        updatedRows = updatedRows.reverse();
      }
    }

    return updatedRows;
  }, [rows, filter, sortColumn, sortAscending]);

  if (!columnOrder.length) {
    return <EmptyTable />;
  }

  const pageRows = getPageRows(page.value, filteredRows, { pageSize, hasPagination });

  return (
    <section className={className}>
      {hasFilter && (
        <div className="filter-input">
          <Input value={filter} onChange={handleFilter} />
        </div>
      )}

      <Table width="100%" borderRadius="6px" shadow="0 0 20px 0 rgba(46,61,73,0.15)" mb={4}>
        <TableHeader
          columns={columnOrder}
          onSelectAll={handleToggleAll}
          sortColumn={sortColumn}
          sortAscending={sortAscending}
          onSort={handleSort}
        />

        <Tbody>
          {pageRows.map((row, idx) => (
            <TableRow
              key={idx}
              row={row}
              columnOrder={columnOrder}
              selected={isRowChecked(row.checkboxId)}
              onPreview={handlePreview}
              onSelect={() => handleToggleSingle(row.checkboxId)}
            />
          ))}
        </Tbody>
      </Table>

      {hasPagination && (
        <Paginator page={page} options={getPaginatorOptions(filteredRows, { pageSize })} onPaginate={handlePaginate} />
      )}

      <Modal isOpen={isOpen} onClose={handlePreviewClose}>
        {preview}
      </Modal>
    </section>
  );
};
